import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Clock, 
  Users, 
  Star, 
  Edit3, 
  MoreVertical, 
  Play, 
  Trophy, 
  Bookmark,
  Timer,
  Target,
  ChevronDown,
  ChevronRight,
  Heart,
  PoundSterling,
  RefreshCw
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import gameService from '../services/gameService';

// Helper function to check if game is co-op based on isCoOp field OR tags
const isCoOpGame = (game) => {
  // Check the isCoOp field first
  if (game?.isCoOp === true) return true;
  
  // Also check tags for cooperative keywords
  const tags = game?.tags || [];
  if (!Array.isArray(tags)) return false;
  const coopKeywords = ['cooperative', 'co-op', 'cooperative game'];
  return tags.some(tag => 
    coopKeywords.some(keyword => 
      tag.toLowerCase().includes(keyword)
    )
  );
};

const GameListItem = ({ game, onEdit, onToggleOwned, onToggleTry, onPlay, onGameClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [priceCooldown, setPriceCooldown] = useState(null);
  const [showTags, setShowTags] = useState(false);
  const [skipNextLoad, setSkipNextLoad] = useState(false);
  
  // Check if game is co-op based on isCoOp field or tags - memoized
  const isCoOp = useMemo(() => isCoOpGame(game), [game.isCoOp, game.tags]);

  // PERFORMANCE: Disable auto-loading of prices on mount to prevent API spam
  // Prices will only load when user explicitly clicks the refresh button
  // This prevents 200+ API calls on initial page load
  useEffect(() => {
    // Skip if we just manually updated
    if (skipNextLoad) {
      setSkipNextLoad(false);
      return;
    }
    
    // Clear price data if game no longer meets criteria
    const shouldShowPrice = game.wishlist || game.try;
    if (!shouldShowPrice) {
      setPriceData(null);
      setPriceCooldown(null);
    }
    // NOTE: Removed automatic loadPriceData() call - now manual only
  }, [game.id, game.bggId, game.wishlist, game.try, skipNextLoad]);

  const loadPriceData = async () => {
    try {
      const price = await gameService.getGamePrice(game.id);
      setPriceData(price);
      
      if (price) {
        const cooldown = await gameService.checkPriceCooldown(game.id);
        setPriceCooldown(cooldown);
      }
    } catch (error) {
      console.error('Error loading price data:', error);
    }
  };

  const handleUpdatePrice = async () => {
    if (!game.bggId || isLoadingPrice) return;
    
    setIsLoadingPrice(true);
    
    try {
      const result = await gameService.updateGamePrice(game.id, game.bggId);
      
      if (result?.success && result?.price_data) {
        // Prevent the useEffect from immediately reloading old data
        setSkipNextLoad(true);
        
        // Force a new object reference to ensure React detects the change
        const newPriceData = { ...result.price_data };
        setPriceData(newPriceData);
        
        // Update cooldown with error handling
        try {
          const cooldown = await gameService.checkPriceCooldown(game.id);
          setPriceCooldown(cooldown);
        } catch (cooldownError) {
          console.error('Error checking price cooldown:', cooldownError);
        }
      } else {
        // Log error but don't break the UI
        console.warn('Price update failed:', result?.message || 'No price data received');
      }
    } catch (error) {
      console.error('Error updating price:', error);
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const shouldShowPriceSection = (game.wishlist || game.try) && game.bggId;

  // Memoize complexity dots calculation
  const complexityDots = useMemo(() => {
    const dots = [];
    const maxDots = 5;
    const filledDots = Math.round(game.complexity);
    
    for (let i = 1; i <= maxDots; i++) {
      const isFilled = i <= filledDots;
      dots.push(
        <div
          key={i}
          className={`w-1.5 h-1.5 rounded-full transition-colors ${
            isFilled 
              ? game.complexity <= 2 
                ? 'bg-emerald-500 dark:bg-emerald-400'
                : game.complexity <= 3 
                ? 'bg-amber-500 dark:bg-amber-400'
                : 'bg-red-500 dark:bg-red-400'
              : 'bg-gray-300 dark:bg-gray-600'
          }`}
        />
      );
    }
    return dots;
  }, [game.complexity]);

  const getComplexityDots = (complexity) => complexityDots;

  // Memoize status info
  const statusInfo = useMemo(() => {
    if (game.owned) return { 
      text: 'Owned', 
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700', 
      icon: Trophy 
    };
    if (game.wishlist) return { 
      text: 'Wishlist', 
      color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700', 
      icon: Bookmark 
    };
    if (game.try) return { 
      text: 'Want to Try', 
      color: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700', 
      icon: Target 
    };
    return { 
      text: 'Owned', 
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700', 
      icon: Trophy 
    };
  }, [game.owned, game.wishlist, game.try]);

  const getStatusInfo = () => statusInfo;
  const StatusIcon = statusInfo.icon;

  return (
    <Card 
      className="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-600 transition-all duration-300 hover:shadow-md overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3 md:gap-4">
          {/* Game Image - Larger on mobile for better UX */}
          <div 
            className="relative w-24 h-24 md:w-16 md:h-16 flex-shrink-0 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-gray-700 dark:to-gray-600 rounded-lg overflow-hidden cursor-pointer"
            onClick={() => onGameClick && onGameClick(game)}
          >
            {game.image && (game.image.startsWith('http') || game.image.startsWith('data:')) && !game.image.includes('placeholder') ? (
              <img 
                src={game.image} 
                alt={game.name}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e) => {
                  // If image fails to load, hide it and show dice placeholder
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div className={`w-full h-full flex items-center justify-center ${game.image && (game.image.startsWith('http') || game.image.startsWith('data:')) && !game.image.includes('placeholder') ? 'hidden' : 'flex'}`}>
              <div className="relative transform rotate-12">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-200 to-amber-200 dark:from-gray-500 dark:to-gray-600 rounded border border-orange-300 dark:border-gray-400 flex items-center justify-center shadow-sm">
                  <div className="text-sm font-bold text-orange-600 dark:text-gray-200 transform -rotate-12">
                    Z
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Game Info - Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile Layout - Name prominently displayed */}
            <div className="md:hidden space-y-2 mb-3">
              {/* Game Name - Large and prominent on mobile */}
              <div 
                className="cursor-pointer"
                onClick={() => onGameClick && onGameClick(game)}
              >
                <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors text-xl leading-tight">
                  {game.name}
                </h3>
              </div>
              
              {/* Badges Row - Below name on mobile */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Status Badge */}
                <Badge className={`${statusInfo.color} text-xs font-medium border shadow-sm`}>
                  {StatusIcon && <StatusIcon size={10} className="mr-1" />}
                  {statusInfo.text}
                </Badge>
                
                {/* Co-op Badge */}
                {isCoOp && (
                  <Badge className="bg-purple-200 dark:bg-purple-800/90 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-600 text-xs font-medium shadow-sm">
                    Co-op
                  </Badge>
                )}
                
                {/* Action Menu - Visible on mobile */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-gray-400 dark:text-gray-500 hover:text-orange-600 dark:hover:text-orange-400 ml-auto"
                      title="More Options"
                    >
                      <MoreVertical size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(game); }} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400">
                      <Edit3 size={12} className="mr-2" />
                      Edit Game
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPlay?.(game); }} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400">
                      <Play size={12} className="mr-2" />
                      Mark as Played
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); onToggleOwned(game.id); }}
                      className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
                    >
                      <Trophy size={12} className="mr-2" />
                      {game.owned ? 'Remove from Owned' : 'Mark as Owned'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); onToggleTry(game.id); }}
                      className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
                    >
                      <Target size={12} className="mr-2" />
                      {game.try ? 'Remove from Try List' : 'Want to Try'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Add to wishlist logic - update game status to wishlist
                        gameService.updateGameStatus(game.id, { 
                          wishlist: true, 
                          owned: false, 
                          try: false 
                        }).then(() => {
                          // Reload the page or refresh data
                          window.location.reload();
                        });
                      }}
                      className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
                    >
                      <Bookmark size={12} className="mr-2" />
                      Add to Wishlist
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            
            {/* Desktop Layout - Original compact design */}
            <div className="hidden md:flex items-start justify-between mb-2">
              <div 
                className="flex-1 min-w-0 pr-2 cursor-pointer"
                onClick={() => onGameClick && onGameClick(game)}
              >
                <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors truncate text-lg">
                  {game.name}
                </h3>
                {game.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-1 mt-1">
                    {game.description}
                  </p>
                )}
              </div>

              {/* Actions - Desktop only */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Status Toggle Icon - Only show for non-owned games */}
                {!game.owned && (
                  <>
                    {game.try ? (
                      // If game is "Want to Try", show wishlist icon (clicking changes to wishlist)
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onToggleOwned(game.id); }}
                        className="h-7 w-7 p-0 transition-colors text-blue-500 hover:text-blue-600"
                        title="Add to Wishlist"
                      >
                        <Bookmark size={12} />
                      </Button>
                    ) : game.wishlist ? (
                      // If game is "Wishlist", show want to try icon (clicking changes to want to try)
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onToggleTry(game.id); }}
                        className="h-7 w-7 p-0 transition-colors text-amber-500 hover:text-amber-600"
                        title="Mark as Want to Try"
                      >
                        <Target size={12} />
                      </Button>
                    ) : null}
                  </>
                )}

                {/* Co-op Badge */}
                {isCoOp && (
                  <Badge className="bg-purple-200 dark:bg-purple-800/90 text-purple-800 dark:text-purple-200 border-purple-300 dark:border-purple-600 text-xs font-medium shadow-sm">
                    Co-op
                  </Badge>
                )}

                {/* Status Badge - Now positioned after icons for better alignment */}
                <Badge className={`${statusInfo.color} text-xs font-medium border cursor-default shadow-sm backdrop-blur-sm`}>
                  {StatusIcon && <StatusIcon size={10} className="mr-1" />}
                  {statusInfo.text}
                </Badge>

                {/* Action Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 w-7 p-0 text-gray-400 dark:text-gray-500 hover:text-orange-600 dark:hover:text-orange-400 transition-opacity ${
                        isHovered ? 'opacity-100' : 'opacity-0'
                      }`}
                      title="More Options"
                    >
                      <MoreVertical size={12} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-lg">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(game); }} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400">
                      <Edit3 size={12} className="mr-2" />
                      Edit Game
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPlay?.(game); }} className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400">
                      <Play size={12} className="mr-2" />
                      Mark as Played
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); onToggleOwned(game.id); }}
                      className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
                    >
                      <Trophy size={12} className="mr-2" />
                      {game.owned ? 'Remove from Owned' : 'Mark as Owned'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); onToggleTry(game.id); }}
                      className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
                    >
                      <Target size={12} className="mr-2" />
                      {game.try ? 'Remove from Try List' : 'Want to Try'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Add to wishlist logic - update game status to wishlist
                        gameService.updateGameStatus(game.id, { 
                          wishlist: true, 
                          owned: false, 
                          try: false 
                        }).then(() => {
                          // Reload the page or refresh data
                          window.location.reload();
                        });
                      }}
                      className="text-gray-700 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400"
                    >
                      <Bookmark size={12} className="mr-2" />
                      Add to Wishlist
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Game Stats - Compact Row */}
            <div className="flex items-center gap-4 md:gap-6 text-sm text-gray-600 dark:text-gray-400 mb-3">
              <div className="flex items-center gap-1">
                <Users size={12} />
                <span>{game.players.min}–{game.players.max}</span>
              </div>
              
              <div className="flex items-center gap-1">
                <Timer size={12} />
                <span>{game.playtime}m</span>
              </div>

              <div className="flex items-center gap-1">
                <div className="flex gap-0.5">
                  {getComplexityDots(game.complexity)}
                </div>
                <span className="ml-1">{Math.floor(game.complexity * 10) / 10}</span>
              </div>

              {/* Rating - Hidden on mobile, visible on desktop */}
              {game.rating && (
                <div className="hidden md:flex items-center gap-1">
                  <Star size={12} className="text-orange-400 fill-current" />
                  <span className="font-medium text-gray-900 dark:text-white">
                    {Math.floor(game.rating * 10) / 10}
                  </span>
                </div>
              )}

              {game.playCount > 0 && (
                <div className="flex items-center gap-1">
                  <Play size={10} />
                  <span className="text-xs">{game.playCount} plays</span>
                </div>
              )}
            </div>

            {/* Price Section - Only for wishlist/want-to-try games */}
            {shouldShowPriceSection && (
              <div className="flex items-center justify-between py-2 border-t border-gray-100 dark:border-gray-700 mt-2">
                <div className="flex items-center gap-2">
                  {priceData && priceData.best_price ? (
                    <div className="space-y-1">
                      {/* Find Amazon UK and Zatu prices separately */}
                      {(() => {
                        const retailers = priceData.retailers || [];
                        const amazon = retailers.find(r => r.name === 'Amazon UK');
                        const zatu = retailers.find(r => r.name === 'Zatu Games');
                        
                        return (
                          <>
                            {/* Amazon UK Price */}
                            {amazon && (
                              <div className="flex items-center gap-1">
                                <PoundSterling size={12} className="text-green-600 dark:text-green-400" />
                                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                  {amazon.price.toFixed(2)}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                                <a
                                  href={amazon.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer"
                                  onClick={(e) => e.stopPropagation()} // Prevent triggering game detail modal
                                >
                                  Amazon UK
                                </a>
                                {amazon.in_stock && (
                                  <Badge variant="outline" className="text-xs px-1 py-0 text-green-600 border-green-600 ml-2">
                                    In Stock
                                  </Badge>
                                )}
                              </div>
                            )}
                            
                            {/* Zatu Games Price */}
                            {zatu && (
                              <div className="flex items-center gap-1">
                                <PoundSterling size={12} className="text-purple-600 dark:text-purple-400" />
                                <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                                  {zatu.price.toFixed(2)}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                                <a
                                  href={zatu.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 underline cursor-pointer"
                                  onClick={(e) => e.stopPropagation()} // Prevent triggering game detail modal
                                >
                                  Zatu Games
                                </a>
                                {zatu.in_stock && (
                                  <Badge variant="outline" className="text-xs px-1 py-0 text-purple-600 border-purple-600 ml-2">
                                    In Stock
                                  </Badge>
                                )}
                              </div>
                            )}
                            
                            {/* Fallback: if neither Amazon nor Zatu, show best price */}
                            {!amazon && !zatu && priceData.retailer_name && (
                              <div className="flex items-center gap-1">
                                <PoundSterling size={12} className="text-green-600 dark:text-green-400" />
                                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                  {priceData.best_price.toFixed(2)}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">at</span>
                                {(() => {
                                  const bestRetailer = retailers.find(r => r.price === priceData.best_price);
                                  return bestRetailer && bestRetailer.link ? (
                                    <a
                                      href={bestRetailer.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline cursor-pointer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {priceData.retailer_name}
                                    </a>
                                  ) : (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{priceData.retailer_name}</span>
                                  );
                                })()}
                                {priceData.in_stock && (
                                  <Badge variant="outline" className="text-xs px-1 py-0 text-green-600 border-green-600 ml-2">
                                    In Stock
                                  </Badge>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    
                    {/* Price Accuracy Disclaimer and Timestamp */}
                    {priceData && (
                      <div className="mt-1 pt-1 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex flex-col gap-1">
                          {/* Timestamp */}
                          {priceData?.updated_at && (
                            <div className="flex items-center gap-1">
                              <Clock size={8} className="text-gray-400" />
                              <span className="text-xs text-gray-400">
                                Updated {new Date(priceData?.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          
                          {/* Accuracy Disclaimer */}
                          <span className="text-xs text-amber-600 dark:text-amber-400 leading-tight">
                            ⚠️ Prices may vary. Click to verify.
                          </span>
                        </div>
                      </div>
                    )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {priceData ? 'No price data available' : 'Click to check price'}
                    </span>
                  )}
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleUpdatePrice(); }}
                  disabled={isLoadingPrice || (priceCooldown && !priceCooldown.can_update)}
                  className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                  title={
                    priceCooldown && !priceCooldown.can_update 
                      ? `Price update available in ${priceCooldown.cooldown_remaining} minutes`
                      : 'Update price'
                  }
                >
                  <RefreshCw 
                    size={12} 
                    className={`${
                      isLoadingPrice ? 'animate-spin' : ''
                    } ${
                      priceCooldown && !priceCooldown.can_update 
                        ? 'text-gray-400 dark:text-gray-600' 
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  />
                </Button>
              </div>
            )}

            {/* Tags - Collapsible */}
            {game.tags && game.tags.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTags(!showTags)}
                    className="h-6 px-2 py-0 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    {showTags ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <span className="ml-1">
                      Tags ({game.tags.length})
                    </span>
                  </Button>
                </div>
                
                {showTags && (
                  <div className="flex flex-wrap gap-1">
                    {game.tags.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map((tag, index) => (
                      <Badge 
                        key={index}
                        variant="secondary" 
                        className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-orange-100 dark:hover:bg-orange-900/30 hover:text-orange-700 dark:hover:text-orange-300 transition-colors border-0 px-2 py-0"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Last Played - Compact */}
            {game.lastPlayed && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Last played: {new Date(game.lastPlayed).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GameListItem;